import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import { Header } from "../components/Layout";
import BackToProjectBanner from "../components/BackToProjectBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Loader2, Trash2, Calendar, CheckCircle2, Circle } from "lucide-react";
import { TaskListSkeleton } from "../components/skeletons";

export default function Tasks() {
  const [searchParams] = useSearchParams();
  const { 
    tasks, 
    fetchTasks, 
    createTask, 
    updateTask,
    updateTaskStatus,
    deleteTask,
    contacts,
    fetchContacts,
    projects,
    fetchProjects,
    loading 
  } = useData();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    due_date: "",
    priority: "medium",
    status: "pending",
    contact_id: "",
    project_id: "",
    assigned_to: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTasks();
    fetchContacts();
    fetchProjects();
    if (searchParams.get("create") === "true") openCreateModal();
  }, [fetchTasks, fetchContacts, fetchProjects, searchParams]);

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === "all" || task.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingTask) {
        await updateTask(editingTask.id, formData);
        toast.success("Task updated!");
      } else {
        await createTask(formData);
        toast.success("Task created!");
      }
      setFormData({
        title: "",
        description: "",
        due_date: "",
        priority: "medium",
        status: "pending",
        contact_id: "",
        project_id: "",
        assigned_to: ""
      });
      setSubmitting(false);
      setShowModal(false);
      setEditingTask(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save task");
      setSubmitting(false);
    }
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || "",
      description: task.description || "",
      due_date: task.due_date ? task.due_date.slice(0, 16) : "",
      priority: task.priority || "medium",
      status: task.status || "pending",
      contact_id: task.contact_id || "",
      project_id: task.project_id || "",
      assigned_to: task.assigned_to || ""
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingTask(null);
    setFormData({
      title: "",
      description: "",
      due_date: "",
      priority: "medium",
      status: "pending",
      contact_id: "",
      project_id: "",
      assigned_to: ""
    });
    setShowModal(true);
  };

  const toggleTaskStatus = async (task) => {
    const newStatus = task.status === "completed" ? "pending" : "completed";
    try {
      await updateTaskStatus(task.id, newStatus);
      toast.success(newStatus === "completed" ? "Task completed!" : "Task reopened");
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await deleteTask(id);
      toast.success("Moved to trash");
      setConfirmDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "text-red-600 bg-red-50 border-red-200";
      case "medium": return "text-amber-600 bg-amber-50 border-amber-200";
      default: return "text-blue-600 bg-blue-50 border-blue-200";
    }
  };

  const getContactName = (contactId) => contacts.find(c => c.id === contactId)?.name || "";
  const getProjectAddress = (projectId) => projects.find(p => p.id === projectId)?.project_address || "";

  return (
    <div className="min-h-screen">
      <BackToProjectBanner />
      <Header title="Tasks">
        <Button
          className="bg-[#0F4C81] hover:bg-[#0D4270] h-12 px-6"
          onClick={openCreateModal}
          data-testid="create-task-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Task
        </Button>
      </Header>

      <div className="p-4 sm:p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-tasks"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-48 h-12">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <TaskListSkeleton rows={5} />
        ) : filteredTasks.length > 0 ? (
          <div className="space-y-3">
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                className={`bg-white rounded-xl p-4 border border-slate-200 hover:shadow-md transition-shadow flex items-center gap-4 cursor-pointer ${
                  task.status === "completed" ? "opacity-60" : ""
                }`}
                data-testid={`task-card-${task.id}`}
                onClick={() => openEditModal(task)}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); toggleTaskStatus(task); }}
                  className="flex-shrink-0"
                  data-testid={`toggle-task-${task.id}`}
                >
                  {task.status === "completed" ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  ) : (
                    <Circle className="w-6 h-6 text-slate-300 hover:text-[#0F4C81]" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`font-medium ${task.status === "completed" ? "line-through text-slate-400" : "text-slate-800"}`}>
                      {task.title}
                    </h3>
                    <Badge className={`text-xs ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </Badge>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    {task.due_date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(task.due_date).toLocaleDateString()}
                      </div>
                    )}
                    {task.contact_id && (
                      <span>Contact: {getContactName(task.contact_id)}</span>
                    )}
                    {task.project_id && (
                      <span>Project: {getProjectAddress(task.project_id)}</span>
                    )}
                    {task.description && (
                      <span className="text-slate-400 truncate max-w-[200px]">{task.description}</span>
                    )}
                  </div>
                </div>

                {confirmDeleteId === task.id ? (
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      className="h-7 px-2 text-xs bg-red-600 hover:bg-red-700 text-white"
                      onClick={(e) => handleDelete(task.id, e)}
                      data-testid={`confirm-delete-task-${task.id}`}
                    >
                      Delete
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(task.id); }}
                    data-testid={`delete-task-${task.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {searchQuery || filterStatus !== "all" ? "No tasks found" : "No tasks yet"}
            </h3>
            <p className="text-slate-500 mb-4">
              {searchQuery || filterStatus !== "all" ? "Try different filters" : "Create your first task"}
            </p>
            <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={openCreateModal}>
              <Plus className="w-5 h-5 mr-2" />Add Task
            </Button>
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) setEditingTask(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTask ? "Edit Task" : "Create Task"}</DialogTitle>
            <DialogDescription>{editingTask ? "Update task details" : "Add a new task to your list"}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Task title"
                required
                className="h-12"
                data-testid="task-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Task description..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="datetime-local"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {editingTask && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link to Contact</Label>
                <Select value={formData.contact_id || "none"} onValueChange={(v) => setFormData({ ...formData, contact_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select contact" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Link to Project</Label>
                <Select value={formData.project_id || "none"} onValueChange={(v) => setFormData({ ...formData, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_address || "No address"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1 h-12" onClick={() => { setShowModal(false); setEditingTask(null); }}>Cancel</Button>
              <Button type="submit" className="flex-1 h-12 bg-[#0F4C81] hover:bg-[#0D4270]" disabled={submitting} data-testid="submit-task-btn">
                {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{editingTask ? "Saving..." : "Creating..."}</> : editingTask ? "Save Changes" : "Create Task"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
